/**
 * SuitDesignerModal — the "Design" flow. Iter 126 rewrite (Jun 2026).
 *
 * · Photoreal preview: layered ghost-mannequin photography compositor
 *   (SuitDesignerPreview) with Open and Closed front views.
 *   Every swap is instant and zero-cost; Gemini only fires on Finalize.
 * · Liner Library: custom liner uploads now (a) actually show in the
 *   preview and (b) can be saved to the fabric library
 *   (garment_category="liners") for reuse across clients.
 * · Finalize renders are cached by design-hash server-side — repeat
 *   finalizes of the same design return instantly at $0.
 * · Mobile: the whole panel scrolls; preview stacks on top.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Sparkles, Loader2, Check, ChevronRight, Upload, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';
import SuitDesignerPreview from './SuitDesignerPreview';
import { ClientLinerPreview } from './ClientLinerPreview';
import {
  LINER_CATALOG, BUTTON_STYLES, BUTTON_COLOURS, LAPEL_TYPES,
  LAPEL_WIDTH_MIN, LAPEL_WIDTH_MAX, LAPEL_WIDTH_DEFAULT,
  VENT_STYLES, POCKET_STYLES, BREAST_POCKET_STYLES, PANEL_MODES,
  linerCssBackground, allowedLapelsFor,
} from './suitDesignerCatalog';

// Re-export for legacy imports.
export * from './suitDesignerCatalog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Iter 148 — RUNTIME ROLE GATE ────────────────────────────────
// The compositor-based designer (Buttons / Lapels / Liner / Vents /
// Pockets) is complete, but the full option set is PUBLISHED only to
// the master admin until it's approved for public release. Everyone
// else gets the Liner-only studio (`fullDesigner={false}`): pick a
// liner (catalog / library / custom upload + panel mode) and render.
const ALL_TABS = [
  { id: 'buttons', label: 'Buttons' },
  { id: 'lapels',  label: 'Lapels' },
  { id: 'liner',   label: 'Liner' },
  { id: 'vents',   label: 'Vents' },
  { id: 'pockets', label: 'Pockets' },
  { id: 'finalize',label: 'Finalize' },
];
const tabsFor = (full) => full
  ? ALL_TABS.filter(t => t.id !== 'finalize')  // full designer keeps ordering, drops finalize
  : ALL_TABS.filter(t => t.id === 'liner');    // published/liner-only mode

// Each tab auto-flips the camera to the view where its options are
// actually visible on the photo.
const TAB_VIEW = {
  buttons: 'closed',
  lapels: 'closed',
  liner: 'open',
  vents: 'closed',
  pockets: 'closed',
  finalize: 'open',
};

const DEFAULT_DESIGN = {
  button_style: '2b',
  button_colour_id: 'horn-dark',
  lapel_type: 'notch',
  lapel_width_in: LAPEL_WIDTH_DEFAULT,
  liner_id: 'burgundy-silk',
  liner_custom_url: null,
  liner_library_id: null,
  panel_mode: 'full',
  vent_style: 'double',
  pocket_style: 'flapped',
  flap_tucked: false,
  breast_pocket_style: 'welt',
};

export default function SuitDesignerModal({
  open,
  onClose,
  fabric,             // the selected fabric object
  clientId,           // active client id
  clientImage,        // client's face photo (b64 or url)
  sourceLookId,       // Iter 128 — the look whose carousel receives the renders
  sourceLookImage,
  sourceLookPath,
  onRendered,         // (finalRenders, sourceLookId) => void — parent injects results
  fullDesigner = false, // Iter 148 — master admin gets the full option set
}) {
  const TABS = tabsFor(fullDesigner);
  const [tab, setTab] = useState('liner');
  const [view, setView] = useState('open'); // open / closed fronts only
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [rendering, setRendering] = useState(false);
  const [uploadingLiner, setUploadingLiner] = useState(false);
  const [savedLiners, setSavedLiners] = useState([]);
  const [savingLiner, setSavingLiner] = useState(false);
  const [linerCategory, setLinerCategory] = useState('');
  const linerPhotoRef = useRef(null);
  const [linerPhotoReady, setLinerPhotoReady] = useState(false);
  const [linerPreviewMode, setLinerPreviewMode] = useState('client');
  const photoLinerMode = !!sourceLookId && tab === 'liner' && linerPreviewMode === 'client';

  const update = useCallback((patch) => setDesign(d => ({ ...d, ...patch })), []);

  const switchTab = useCallback((id) => {
    setTab(id);
    if (TAB_VIEW[id]) setView(TAB_VIEW[id]);
  }, []);

  // Saved liner library — fabrics tagged garment_category="liners".
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/fabrics`, {
          params: { garment_category: 'liners', limit: 200 },
          withCredentials: true,
        });
        if (!cancelled) setSavedLiners(data.fabrics || []);
      } catch { /* silent — built-in liners still work */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleLinerUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Image is over 8 MB — please pick a smaller one'); return; }
    setUploadingLiner(true);
    const reader = new FileReader();
    reader.onload = () => {
      update({ liner_custom_url: reader.result, liner_id: 'custom', liner_library_id: null });
      setUploadingLiner(false);
      setView('open');
      toast.success('Custom liner applied — check the preview');
    };
    reader.onerror = () => { toast.error('Upload failed'); setUploadingLiner(false); };
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  const saveLinerToLibrary = async () => {
    if (!design.liner_custom_url?.startsWith('data:')) return;
    setSavingLiner(true);
    try {
      const b64 = design.liner_custom_url.split(',', 2)[1];
      const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'image/jpeg' });
      const fd = new FormData();
      fd.append('image', blob, 'liner.jpg');
      fd.append('name', `Custom Liner ${new Date().toLocaleDateString()}`);
      fd.append('category', linerCategory.trim());
      const { data } = await axios.post(`${BACKEND_URL}/api/liners`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const liner = data.liner;
      setSavedLiners(prev => [liner, ...prev]);
      update({ liner_library_id: liner.id });
      toast.success(`Liner saved to your library${liner.category ? ` · ${liner.category}` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save liner');
    }
    setSavingLiner(false);
  };

  const selectSavedLiner = (l) => {
    update({
      liner_id: 'custom',
      liner_library_id: l.id,
      liner_custom_url: `${BACKEND_URL}${l.image_url}`,
    });
    setView('open');
  };

  const finalize = async () => {
    if (photoLinerMode) {
      setRendering(true);
      try {
        const composite = linerPhotoRef.current?.getComposite();
        if (!composite) throw new Error('Wait for your jacket photo');
        const selected = savedLiners.find(l => l.id === design.liner_library_id)
          || LINER_CATALOG.find(l => l.id === design.liner_id);
        const { data } = await axios.post(`${BACKEND_URL}/api/liner-preview/save`, {
          ...composite,
          selection: { liner_id: design.liner_id, liner_library_id: design.liner_library_id,
            panel_mode: design.panel_mode, label: selected?.name || 'Custom liner' },
        }, { withCredentials: true, timeout: 60000 });
        onRendered?.(data.looks, data.source_look_id);
        toast.success('Liner saved to this look');
        onClose?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message || 'Could not save liner');
      } finally { setRendering(false); }
      return;
    }
    if (!clientImage) { toast.error('Client photo missing'); return; }
    if (!fabric?.name && !fabric?.id) { toast.error('Fabric context missing'); return; }
    setRendering(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/liner-reveal-render`, {
        fabric_id: fabric?.id || null,
        fabric_ref: fabric?.id ? null : {
          name: fabric?.name,
          sku: fabric?.sku,
          composition: fabric?.composition,
          color_family: fabric?.color_family,
          primary_color_hex: fabric?.primary_color_hex,
          texture_notes: fabric?.texture_notes,
        },
        client_id: clientId,
        client_image: clientImage,
        source_look_id: sourceLookId || null,
        design,
      }, { withCredentials: true, timeout: 180000 });
      const looks = resp.data?.looks || [];
      const mergedLookId = resp.data?.source_look_id || null;
      if (onRendered) onRendered(looks, mergedLookId);
      toast.success(resp.data?.cached
        ? 'Same design already rendered — served instantly from cache'
        : mergedLookId
          ? 'Liner rendered · added to the look\'s carousel'
          : 'Designed suit rendered · added to the gallery');
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error('Session expired — please log in again and retry');
      } else {
        toast.error(e?.response?.data?.detail || 'Render failed — please try again');
      }
    } finally {
      setRendering(false);
    }
  };

  const summary = useMemo(() => _buildSummary(design, savedLiners, fullDesigner), [design, savedLiners, fullDesigner]);

  // Iter 129k — compact liner-swatch preview for the FinalizeTab
  // button. Prefers (1) a live custom upload data URL, (2) a saved
  // library liner image, (3) the catalog swatch hex fallback.
  const linerPreview = useMemo(() => {
    if (design.liner_custom_url) {
      return { src: design.liner_custom_url, label: 'Custom liner' };
    }
    if (design.liner_library_id) {
      const l = savedLiners.find(s => s.id === design.liner_library_id);
      if (l) {
        const src = l.image_url ? (l.image_url.startsWith('http') ? l.image_url : `${BACKEND_URL}${l.image_url}`) : null;
        return { src, label: l.name || 'Saved liner', hex: l.primary_color_hex };
      }
    }
    const cat = LINER_CATALOG.find(l => l.id === design.liner_id);
    if (cat) return { src: null, label: cat.name, hex: cat.color };
    return { src: null, label: 'Liner' };
  }, [design.liner_custom_url, design.liner_library_id, design.liner_id, savedLiners]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 flex items-start md:items-center justify-center p-0 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="suit-designer-modal"
    >
      <div
        className={`w-full ${fullDesigner ? 'max-w-6xl md:h-[90vh]' : 'max-w-3xl md:h-[85vh] md:max-h-[92vh]'} bg-[#0A0A0C] border border-[#27272A] flex flex-col md:flex-row overflow-hidden shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── LEFT: PHOTOREAL PREVIEW ─────────────────────────────
            In slim (Liner Studio) mode the preview column now takes
            the full modal height and uses a soft-grey studio backdrop
            (matching the base photos) so no black bars are visible
            around the portrait aspect. */}
        <div className={`relative shrink-0 border-b md:border-b-0 md:border-r border-[#27272A] ${fullDesigner ? 'h-[46vh] md:h-full md:flex-1' : 'h-[340px] md:h-full md:w-[380px] bg-[#E8E8EA]'}`}>
          <button
            className="absolute top-3 left-3 text-white/80 hover:text-white p-2 z-10 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            data-testid="designer-close"
            aria-label="Close designer"
          >
            <X className="w-5 h-5" />
          </button>
          {fullDesigner && !photoLinerMode && (
            <div className="absolute top-4 right-4 z-10 flex bg-[#0A0A0C]/85 backdrop-blur-sm border border-[#27272A]">
              {[
                { id: 'open',   label: 'Open' },
                { id: 'closed', label: 'Closed' },
              ].map(v => (
                <button
                  key={v.id}
                  className={`px-3 h-8 text-[10px] uppercase tracking-widest transition-colors ${view === v.id ? 'bg-[#C2A878] text-[#0A0A0C]' : 'text-[#F5F5F5] hover:text-[#C2A878]'}`}
                  onClick={() => setView(v.id)}
                  data-testid={`designer-view-${v.id}`}
                >{v.label}</button>
              ))}
            </div>
          )}
          {photoLinerMode ? (
            <ClientLinerPreview ref={linerPhotoRef} sourceLookId={sourceLookId}
              sourceImage={sourceLookImage} sourcePath={sourceLookPath} design={design} savedLiners={savedLiners}
              onReady={setLinerPhotoReady} />
          ) : <SuitDesignerPreview fabric={fabric} design={design} view={view} />}
        </div>

        {/* ── RIGHT: CONTROLS ─────────────────────────────────── */}
        <div className={`w-full flex flex-col ${fullDesigner ? 'md:w-[440px] md:h-full' : 'md:flex-1 md:h-full'} bg-[#0A0A0C]`}>
          {/* Fabric header */}
          <div className="px-5 py-4 border-b border-[#27272A]">
            <div className="text-[10px] uppercase tracking-widest text-[#52525B]">Designing on</div>
            <div className="text-sm text-[#F5F5F5] mt-0.5 font-medium" data-testid="designer-fabric-name">{fabric?.name || 'Selected fabric'}</div>
            <div className="text-[10px] text-[#A1A1AA] mt-1">{fabric?.sku} · {fabric?.composition}</div>
            {sourceLookId && fullDesigner && tab === 'liner' && (
              <div className="inline-flex border border-[#27272A] mt-3" data-testid="liner-preview-subject">
                {[['client', 'Client'], ['jacket', 'Jacket']].map(([id, label]) => (
                  <button key={id} onClick={() => setLinerPreviewMode(id)} aria-pressed={linerPreviewMode === id}
                    className={`px-4 h-8 text-xs transition-colors ${linerPreviewMode === id ? 'bg-[#C2A878] text-black' : 'text-[#A1A1AA] hover:text-white'}`}
                    data-testid={`liner-preview-${id}`}>{label}</button>
                ))}
              </div>
            )}
          </div>

          {/* Tab strip — sticky on mobile so navigation survives scrolling */}
          <div className="flex overflow-x-auto border-b border-[#27272A] scrollbar-hide sticky top-0 z-10 bg-[#0A0A0C] md:static">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                className={`px-4 h-11 text-[10px] uppercase tracking-widest whitespace-nowrap border-b-2 ${tab === t.id ? 'border-[#C2A878] text-[#F5F5F5]' : 'border-transparent text-[#52525B] hover:text-[#A1A1AA]'}`}
                data-testid={`designer-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab body — scrolls internally on desktop, flows with the page on mobile */}
          <div className="md:flex-1 md:overflow-y-auto px-5 py-5 pb-8" data-testid="designer-tab-body">
            {tab === 'buttons' && <ButtonsTab design={design} update={patch => {
              update(patch);
              // Inside-out open photographs hide the fastening buttons.
              // Show their real exterior face instead of inventing discs
              // on the exposed lining when a button option is selected.
              setView('closed');
            }} />}
            {tab === 'lapels' && <LapelsTab design={design} update={update} />}
            {tab === 'liner' && (
              <LinerTab
                design={design}
                update={update}
                onCustomUpload={handleLinerUpload}
                uploading={uploadingLiner}
                savedLiners={savedLiners}
                onSelectSaved={selectSavedLiner}
                onSaveToLibrary={saveLinerToLibrary}
                savingLiner={savingLiner}
                linerCategory={linerCategory}
                setLinerCategory={setLinerCategory}
              />
            )}
            {tab === 'vents' && <VentsTab design={design} update={update} />}
            {tab === 'pockets' && <PocketsTab design={design} update={update} />}
          </div>

          {/* Footer nav — Next on any non-last tab, Render (with liner
              swatch preview) on the LAST tab so the tailor never has to
              hunt for a separate Finalize screen. */}
          {(() => {
            const tabIdx = TABS.findIndex(t => t.id === tab);
            const isLastTab = tabIdx === TABS.length - 1;
            return (
              <div className="px-5 py-4 border-t border-[#27272A] flex items-center justify-between gap-3 sticky bottom-0 bg-[#0A0A0C] md:static z-10">
                <div className="text-[10px] uppercase tracking-widest text-[#52525B] truncate" data-testid="designer-summary">{summary}</div>
                {isLastTab || photoLinerMode ? (
                  <Button
                    onClick={finalize}
                    disabled={rendering || (photoLinerMode ? !linerPhotoReady : !clientImage)}
                    title={photoLinerMode ? 'Save this liner to the selected look' : !clientImage ? 'Upload a client photo first' : undefined}
                    className="h-10 px-4 rounded-none uppercase tracking-widest text-[10px] font-semibold shrink-0 flex items-center gap-2 disabled:opacity-40"
                    style={{ backgroundColor: '#C2A878', color: '#0A0A0C' }}
                    data-testid="finalize-render-btn"
                  >
                    {linerPreview?.src ? (
                      <img
                        src={linerPreview.src}
                        alt={linerPreview.label}
                        className="w-6 h-6 object-cover border border-black/30 shrink-0"
                        data-testid="finalize-liner-swatch-img"
                      />
                    ) : (
                      <span
                        className="w-6 h-6 border border-black/30 shrink-0"
                        style={linerPreview?.hex ? { backgroundColor: linerPreview.hex } : {}}
                        data-testid="finalize-liner-swatch-hex"
                      />
                    )}
                    {rendering ? (
                      <><Loader2 className="w-3.5 h-3.5 spinner" /> {photoLinerMode ? 'Saving…' : 'Rendering…'}</>
                    ) : (
                      <>{photoLinerMode ? <Save className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />} {photoLinerMode ? 'Save liner' : 'Render'}</>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => switchTab(TABS[Math.min(TABS.length - 1, tabIdx + 1)].id)}
                    className="h-9 px-4 rounded-none uppercase tracking-widest text-[10px] font-semibold shrink-0"
                    style={{ backgroundColor: '#C2A878', color: '#0A0A0C' }}
                    data-testid="designer-next"
                  >
                    Next <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function _buildSummary(d, savedLiners = [], fullDesigner = false) {
  let lin;
  if (d.liner_library_id) {
    lin = savedLiners.find(l => l.id === d.liner_library_id)?.name || 'library liner';
  } else if (d.liner_custom_url) {
    lin = 'custom liner';
  } else {
    lin = LINER_CATALOG.find(l => l.id === d.liner_id)?.name || 'liner';
  }
  if (!fullDesigner) return `Liner · ${lin}`;
  const btn = BUTTON_STYLES.find(b => b.id === d.button_style)?.label || 'buttons';
  const lap = d.lapel_type === 'none'
    ? 'no lapel'
    : `${d.lapel_type} lapel · ${d.lapel_width_in.toFixed(2)}"`;
  return `${btn} · ${lap} · ${lin}`;
}

// ─── SUB-TABS ────────────────────────────────────────────────────
function ButtonsTab({ design, update }) {
  const pickStyle = (id) => {
    const allowed = allowedLapelsFor(id);
    if (!allowed.includes(design.lapel_type)) {
      update({ button_style: id, lapel_type: allowed[0] });
    } else {
      update({ button_style: id });
    }
  };
  return (
    <div className="space-y-5">
      <div>
        <Label className="label-luxury mb-2 block">Style</Label>
        <div className="grid grid-cols-1 gap-2" data-testid="buttons-style-grid">
          {BUTTON_STYLES.filter(b => !b.unavailable).map(b => (
            <button
              key={b.id}
              onClick={() => pickStyle(b.id)}
              className={`text-left px-3 py-2.5 border ${design.button_style === b.id ? 'border-[#C2A878] bg-[#1A1A1E]' : 'border-[#27272A] hover:border-[#52525B]'}`}
              data-testid={`btn-style-${b.id}`}
            >
              <div className="flex items-center gap-2">
                {design.button_style === b.id && <Check className="w-3 h-3 text-[#C2A878]" />}
                <span className="text-sm text-[#F5F5F5]">{b.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="label-luxury mb-2 block">Button Colour</Label>
        <div className="grid grid-cols-4 gap-2" data-testid="button-colour-grid">
          {BUTTON_COLOURS.map(c => (
            <button
              key={c.id}
              onClick={() => update({ button_colour_id: c.id })}
              className={`flex flex-col items-center gap-1 p-1.5 border ${design.button_colour_id === c.id ? 'border-[#C2A878]' : 'border-[#27272A] hover:border-[#52525B]'}`}
              title={c.label}
              data-testid={`btn-colour-${c.id}`}
            >
              <div className="w-8 h-8 rounded-full border border-[#0A0A0C]" style={{ backgroundColor: c.hex || 'transparent', backgroundImage: !c.hex ? 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%)' : 'none', backgroundSize: '6px 6px' }} />
              <div className="text-[9px] uppercase tracking-wider text-[#A1A1AA] text-center leading-tight">{c.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LapelsTab({ design, update }) {
  const allowed = allowedLapelsFor(design.button_style);
  return (
    <div className="space-y-5">
      <div>
        <Label className="label-luxury mb-2 block">Style</Label>
        <div className="grid grid-cols-1 gap-2">
          {LAPEL_TYPES.filter(l => allowed.includes(l.id)).map(l => (
            <button
              key={l.id}
              onClick={() => update({ lapel_type: l.id })}
              className={`text-left px-3 py-2.5 border ${design.lapel_type === l.id ? 'border-[#C2A878] bg-[#1A1A1E]' : 'border-[#27272A] hover:border-[#52525B]'}`}
              data-testid={`lapel-type-${l.id}`}
            >
              <div className="text-sm text-[#F5F5F5]">{l.label}</div>
              <div className="text-[10px] text-[#52525B] mt-0.5">{l.desc}</div>
            </button>
          ))}
        </div>
      </div>
      {design.lapel_type !== 'none' && (
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="label-luxury">Width</Label>
          <span className="text-xs text-[#F5F5F5]" data-testid="lapel-width-value">{design.lapel_width_in.toFixed(2)}"</span>
        </div>
        <input
          type="range"
          min={LAPEL_WIDTH_MIN}
          max={LAPEL_WIDTH_MAX}
          step={0.125}
          value={design.lapel_width_in}
          onChange={(e) => update({ lapel_width_in: Number(e.target.value) })}
          className="w-full accent-[#C2A878]"
          data-testid="lapel-width-slider"
          aria-label="Lapel width in inches"
        />
        <div className="flex justify-between text-[9px] uppercase tracking-widest text-[#52525B] mt-1">
          <span>Skinny · 2.25"</span>
          <span>Standard · 3.5"</span>
          <span>Wide · 5"</span>
        </div>
        <p className="text-[10px] text-[#52525B] mt-2">
          Width is applied precisely on the final AI render — the preview shows the lapel style.
        </p>
      </div>
      )}
    </div>
  );
}

function LinerTab({ design, update, onCustomUpload, uploading, savedLiners, onSelectSaved, onSaveToLibrary, savingLiner, linerCategory, setLinerCategory }) {
  const customUnsaved = design.liner_custom_url?.startsWith('data:') && !design.liner_library_id;
  // Iter 127 — group the saved library by subcategory.
  const grouped = React.useMemo(() => {
    const map = new Map();
    savedLiners.forEach(l => {
      const cat = (l.category || 'Custom Liners').trim() || 'Custom Liners';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(l);
    });
    return Array.from(map.entries());
  }, [savedLiners]);
  const existingCategories = grouped.map(([cat]) => cat);
  return (
    <div className="space-y-5">
      <div>
        <Label className="label-luxury mb-2 block">Common Liners</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="liner-catalog-grid">
          {LINER_CATALOG.map(l => (
            <button
              key={l.id}
              onClick={() => update({ liner_id: l.id, liner_custom_url: null, liner_library_id: null })}
              className={`aspect-square border ${design.liner_id === l.id && !design.liner_custom_url ? 'border-[#C2A878]' : 'border-[#27272A] hover:border-[#52525B]'} relative overflow-hidden`}
              title={l.name}
              data-testid={`liner-${l.id}`}
              style={{ background: linerCssBackground(l) }}
            >
              <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[9px] uppercase tracking-wider text-white px-1 py-0.5 text-center leading-tight">{l.name}</div>
            </button>
          ))}
        </div>
      </div>

      {grouped.length > 0 && (
        <div className="border-t border-[#27272A] pt-4">
          <Label className="label-luxury mb-2 block">My Liner Library</Label>
          <div className="space-y-3" data-testid="liner-library-grid">
            {grouped.map(([cat, liners]) => (
              <div key={cat} data-testid={`liner-lib-group-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="text-[9px] uppercase tracking-widest text-[#52525B] mb-1.5">{cat} · {liners.length}</div>
                <div className="grid grid-cols-3 gap-2">
                  {liners.map(l => (
                    <button
                      key={l.id}
                      onClick={() => onSelectSaved(l)}
                      className={`aspect-square border relative overflow-hidden ${design.liner_library_id === l.id ? 'border-[#C2A878]' : 'border-[#27272A] hover:border-[#52525B]'}`}
                      title={l.name}
                      data-testid={`liner-lib-${l.id}`}
                    >
                      <img src={`${BACKEND_URL}${l.image_url}`} alt={l.name} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[9px] uppercase tracking-wider text-white px-1 py-0.5 text-center leading-tight truncate">{l.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-[#27272A] pt-4">
        <Label className="label-luxury mb-2 block">Custom Liner</Label>
        <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-[#52525B] hover:border-[#C2A878] cursor-pointer text-xs text-[#A1A1AA]" data-testid="liner-custom-upload-label">
          {uploading ? <Loader2 className="w-4 h-4 spinner" /> : <Upload className="w-4 h-4" />}
          <span>Upload your own liner image</span>
          <input type="file" accept="image/*" className="hidden" onChange={onCustomUpload} data-testid="liner-custom-upload" />
        </label>
        {design.liner_custom_url && (
          <div className="mt-3 space-y-2">
            <div className="w-full h-24 bg-cover bg-center border border-[#27272A]" style={{ backgroundImage: `url(${design.liner_custom_url})` }} data-testid="liner-custom-preview" />
            {customUnsaved && (
              <div className="space-y-2">
                <input
                  type="text"
                  list="liner-category-options"
                  value={linerCategory}
                  onChange={(e) => setLinerCategory(e.target.value)}
                  placeholder="Subcategory (e.g. Paisley, Team Colors) — optional"
                  className="w-full bg-[#0A0A0C] border border-[#27272A] focus:border-[#C2A878] px-3 py-2 text-xs text-[#F5F5F5] outline-none"
                  data-testid="liner-category-input"
                />
                <datalist id="liner-category-options">
                  {existingCategories.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
            )}
            <div className="flex items-center gap-3">
              {customUnsaved && (
                <button
                  onClick={onSaveToLibrary}
                  disabled={savingLiner}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#C2A878] hover:text-[#D4BE94] disabled:opacity-50"
                  data-testid="liner-save-to-library"
                >
                  {savingLiner ? <Loader2 className="w-3 h-3 spinner" /> : <Save className="w-3 h-3" />}
                  Save to my liner library
                </button>
              )}
              {design.liner_library_id && (
                <span className="text-[10px] uppercase tracking-wider text-[#52525B]" data-testid="liner-saved-badge">
                  <Check className="w-3 h-3 inline mr-1 text-[#C2A878]" />Saved in library
                </span>
              )}
              <button
                onClick={() => update({ liner_custom_url: null, liner_library_id: null, liner_id: LINER_CATALOG[0].id })}
                className="text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:text-[#F5F5F5]"
                data-testid="liner-custom-clear"
              >
                Clear custom
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Panel modes — apply to ANY image-based liner (fresh custom
          upload OR saved library liner). The choice is mirrored in the
          live preview and honoured by the final render compositor
          (Iter 148 — previously hidden for library liners). */}
      {(design.liner_custom_url || design.liner_library_id) && (
        <div className="border-t border-[#27272A] pt-4" data-testid="panel-mode-section">
          <Label className="label-luxury mb-2 block">Panel Mode</Label>
          <div className="grid grid-cols-1 gap-2">
            {PANEL_MODES.map(m => (
              <button
                key={m.id}
                onClick={() => update({ panel_mode: m.id })}
                className={`text-left px-3 py-2 border ${design.panel_mode === m.id ? 'border-[#C2A878] bg-[#1A1A1E]' : 'border-[#27272A] hover:border-[#52525B]'}`}
                data-testid={`panel-mode-${m.id}`}
              >
                <div className="text-sm text-[#F5F5F5]">{m.label}</div>
                <div className="text-[10px] text-[#52525B] mt-0.5">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VentsTab({ design, update }) {
  return (
    <div className="space-y-3">
      {VENT_STYLES.map(v => (
        <button
          key={v.id}
          onClick={() => update({ vent_style: v.id })}
          className={`w-full text-left px-3 py-2.5 border ${design.vent_style === v.id ? 'border-[#C2A878] bg-[#1A1A1E]' : 'border-[#27272A] hover:border-[#52525B]'}`}
          data-testid={`vent-${v.id}`}
        >
          <div className="text-sm text-[#F5F5F5]">{v.label}</div>
          <div className="text-[10px] text-[#52525B] mt-0.5">{v.desc}</div>
        </button>
      ))}
    </div>
  );
}

function PocketsTab({ design, update }) {
  return (
    <div className="space-y-5">
      <div>
        <Label className="label-luxury mb-2 block">Hip Pockets</Label>
        <div className="grid grid-cols-1 gap-2">
          {POCKET_STYLES.map(p => (
            <button
              key={p.id}
              onClick={() => update({ pocket_style: p.id })}
              className={`text-left px-3 py-2.5 border ${design.pocket_style === p.id ? 'border-[#C2A878] bg-[#1A1A1E]' : 'border-[#27272A] hover:border-[#52525B]'}`}
              data-testid={`pocket-${p.id}`}
            >
              <div className="text-sm text-[#F5F5F5]">{p.label}</div>
              <div className="text-[10px] text-[#52525B] mt-0.5">{p.desc}</div>
            </button>
          ))}
        </div>
        {design.pocket_style === 'flapped' && (
          <label className="flex items-center gap-2 mt-3 text-xs text-[#A1A1AA] cursor-pointer" data-testid="flap-tucked-label">
            <input
              type="checkbox"
              checked={!!design.flap_tucked}
              onChange={(e) => update({ flap_tucked: e.target.checked })}
              className="accent-[#C2A878]"
              data-testid="flap-tucked-checkbox"
            />
            Tuck the flaps in (evening / dressier look)
          </label>
        )}
      </div>
      <div className="border-t border-[#27272A] pt-4">
        <Label className="label-luxury mb-2 block">Breast Pocket</Label>
        <div className="grid grid-cols-3 gap-2">
          {BREAST_POCKET_STYLES.map(b => (
            <button
              key={b.id}
              onClick={() => update({ breast_pocket_style: b.id })}
              className={`px-2 py-2 border text-center ${design.breast_pocket_style === b.id ? 'border-[#C2A878] bg-[#1A1A1E]' : 'border-[#27272A] hover:border-[#52525B]'}`}
              data-testid={`breast-${b.id}`}
            >
              <div className="text-xs text-[#F5F5F5]">{b.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FinalizeTab({ onFinalize, rendering, clientImage, linerPreview }) {
  // Iter 129k — the finalize step now shows a single compact render
  // button with an inline swatch preview of the chosen liner. The
  // separate "Design Summary" text block and the "This will render
  // two final images" explanation have been removed at the user's
  // request — the button + swatch is enough context.
  const swatchStyle = linerPreview?.hex
    ? { backgroundColor: linerPreview.hex }
    : {};
  return (
    <div className="space-y-4">
      {!clientImage && (
        <div className="text-[11px] uppercase tracking-wider text-[#F97316] border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-2" data-testid="finalize-no-client">
          Client photo missing — select or upload a client photo first.
        </div>
      )}
      <Button
        onClick={onFinalize}
        disabled={rendering || !clientImage}
        className="w-full h-14 rounded-none uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-3 disabled:opacity-40"
        style={{ backgroundColor: '#C2A878', color: '#0A0A0C' }}
        data-testid="finalize-render-btn"
      >
        {/* Liner swatch preview inside the button. */}
        {linerPreview?.src ? (
          <img
            src={linerPreview.src}
            alt={linerPreview.label}
            className="w-8 h-8 object-cover border border-black/30 shrink-0"
            data-testid="finalize-liner-swatch-img"
          />
        ) : (
          <span
            className="w-8 h-8 border border-black/30 shrink-0"
            style={swatchStyle}
            data-testid="finalize-liner-swatch-hex"
          />
        )}
        {rendering ? (
          <><Loader2 className="w-4 h-4 spinner" /> Rendering the editorial…</>
        ) : (
          <><Sparkles className="w-4 h-4" /> Render With This Liner</>
        )}
      </Button>
    </div>
  );
}

